import { FC, useEffect, useRef, useState } from 'react'

import { useSetAtom } from 'jotai'
import { match } from 'ts-pattern'
import {
  Text,
  BorderRadius6,
  GreyGrey10,
  GreyGrey80,
  IconExclamationTriangle,
  Primary12Regular,
  Primary12SemiBold,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  useNode,
  ValidationLevel,
  NODE_VALIDATION_FIELD_ID,
  WORKFLOW_VALIDATION_KEY
} from 'Automations/nodeDefinitions'
import { NodeIconTemplate } from 'Automations/nodeDefinitions/components'
import {
  selectedNodeFieldAtom,
  selectedNodeIdsAtom
} from 'Automations/automationEditor/atoms'

type Props = {
  nodeId: string
  fieldId: string
  title: string
  message: string
  level: ValidationLevel
}

export const ValidationResult: FC<Props> = ({
  nodeId,
  fieldId,
  title,
  message,
  level
}) => {
  const { definition } = useNode({ nodeId })
  const setSelectedNodeField = useSetAtom(selectedNodeFieldAtom)
  const setSelectedNodeIds = useSetAtom(selectedNodeIdsAtom)
  const textRef = useRef<HTMLParagraphElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const observer = new ResizeObserver(() => {
      setIsOverflowing(el.clientWidth < el.scrollWidth)
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  // Workflow-level results (e.g. generation failure, no-op workflow) are not
  // tied to a node: there is no definition to draw an icon from and nothing to
  // select on click.
  const isWorkflowLevel = nodeId === WORKFLOW_VALIDATION_KEY

  if (!isWorkflowLevel && !definition) return null

  const iconTheme = match(level)
    .with('ERROR', () => 'red' as const)
    .with('WARN', () => 'yellow' as const)
    .exhaustive()

  const NodeIcon = definition?.icon

  return (
    <Tooltip
      label={
        <Box
          as="span"
          data-testid={`automations.editor.workflowErrors.tooltip.${nodeId}.${fieldId}`}
        >
          {title}: {message}
        </Box>
      }
      isDisabled={!isOverflowing}
      shouldWrapChildren={false}
    >
      <Flex
        data-testid={`automations.editor.workflowErrors.result.${nodeId}.${fieldId}`}
        onClick={() => {
          if (isWorkflowLevel) return

          setSelectedNodeIds([nodeId])

          if (fieldId === NODE_VALIDATION_FIELD_ID) return
          setSelectedNodeField({ nodeId, fieldId })
        }}
        _hover={{ background: GreyGrey10 }}
        cursor={isWorkflowLevel ? 'default' : 'pointer'}
        padding="4px"
        gap="8px"
        alignItems="center"
        overflow="hidden"
        borderRadius={BorderRadius6}
      >
        {NodeIcon ? (
          <NodeIcon boxSize="24px" theme={iconTheme} />
        ) : (
          <NodeIconTemplate
            boxSize="24px"
            theme={iconTheme}
            icon={<IconExclamationTriangle />}
          />
        )}

        <Text
          ref={textRef}
          noOfLines={1}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text as="span" {...Primary12SemiBold} color={GreyGrey80}>
            {title}:{' '}
            <Text as="span" {...Primary12Regular} color={GreyGrey80}>
              {message}
            </Text>
          </Text>
        </Text>
      </Flex>
    </Tooltip>
  )
}
